// Shared ProseMirror hook for message drafts.

import { useEffect, useReducer, useRef } from 'react';
import {
  EditorState,
  EditorView,
  MessageMarkdownSerializer,
  MessageMarkdownTransformer,
  buildEditor,
  createMenuHandlers,
  createMenuStateCheckers,
  messageSchema,
} from '@kimcchq/prosemirror-schema';
import { stripTextStyleTags } from '@/lib/sanitize';

// No colour mark: no composer offers colours and helpdesks show its [text:…] markup raw; built off the library's own Schema class, as a second prosemirror-model copy is rejected.
const draftSchema = new messageSchema.constructor({
  nodes: messageSchema.spec.nodes,
  marks: messageSchema.spec.marks.remove('textDecoration'),
});

export type EditorCommand = 'strong' | 'em' | 'code' | 'bulletList' | 'orderedList' | 'undo' | 'redo';

/** Commands whose handler IS the command instead of returning one. */
const DIRECT_COMMANDS = new Set(['undo', 'redo']);

interface UseMessageEditorOptions {
  /** Markdown loaded once per editorKey — later prop changes never reset the doc. */
  initialMarkdown: string;
  /** Bump to load a different draft (e.g. another response version). */
  editorKey: string;
  placeholder?: string;
  onChange?: (markdown: string) => void;
}

export function useMessageEditor({ initialMarkdown, editorKey, placeholder, onChange }: UseMessageEditorOptions) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const handlersRef = useRef<ReturnType<typeof createMenuHandlers> | null>(null);
  const checkersRef = useRef<ReturnType<typeof createMenuStateCheckers> | null>(null);
  // Toolbar state derives from the view on render; transactions just trigger a re-render.
  const [, bumpToolbar] = useReducer((count: number) => count + 1, 0);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const initialRef = useRef(initialMarkdown);
  initialRef.current = initialMarkdown;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const state = EditorState.create({
      // A stored draft can still carry colour markup, which this schema cannot load.
      doc: new MessageMarkdownTransformer(draftSchema).parse(stripTextStyleTags(initialRef.current)),
      plugins: buildEditor({ schema: draftSchema, placeholder, includeMenuBar: false }),
    });

    const view = new EditorView(host, {
      state,
      dispatchTransaction: (tr) => {
        const next = view.state.apply(tr);
        view.updateState(next);
        bumpToolbar();
        if (tr.docChanged) onChangeRef.current?.(MessageMarkdownSerializer.serialize(next.doc));
      },
    });

    viewRef.current = view;
    handlersRef.current = createMenuHandlers(draftSchema, {});
    checkersRef.current = createMenuStateCheckers(draftSchema);
    view.focus();

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [editorKey, placeholder]);

  const exec = (name: EditorCommand) => {
    const view = viewRef.current;
    const handler = handlersRef.current?.[name];
    if (!view || !handler) return;
    const command = DIRECT_COMMANDS.has(name)
      ? (handler as (state: EditorState, dispatch: EditorView['dispatch']) => boolean)
      : (handler as () => (state: EditorState, dispatch: EditorView['dispatch']) => boolean)();
    command(view.state, view.dispatch.bind(view));
    view.focus();
  };

  const isActive = (name: EditorCommand) => {
    const view = viewRef.current;
    const checker = checkersRef.current?.[name];
    if (!view || !checker) return false;
    try {
      return checker(view.state);
    } catch {
      return false;
    }
  };

  /** Insert plain text at the cursor — how the emoji picker writes into the doc. */
  const insertText = (text: string) => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch(view.state.tr.insertText(text));
    view.focus();
  };

  /** Link the selection; with nothing selected, insert the URL as its own linked text. */
  const setLink = (href: string) => {
    const view = viewRef.current;
    const linkMark = draftSchema.marks.link;
    if (!view || !linkMark) return;

    const value = href.trim();
    const normalized = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
    try {
      const url = new URL(normalized);
      if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) return;
    } catch {
      return;
    }

    const { selection, tr } = view.state;
    const { from, to, empty } = selection;
    if (empty) {
      tr.insertText(normalized, from);
      tr.addMark(from, from + normalized.length, linkMark.create({ href: normalized }));
    } else {
      tr.addMark(from, to, linkMark.create({ href: normalized }));
    }
    view.dispatch(tr);
    view.focus();
  };

  const getMarkdown = () => {
    const view = viewRef.current;
    return view ? MessageMarkdownSerializer.serialize(view.state.doc) : initialRef.current;
  };

  return { hostRef, exec, isActive, insertText, setLink, getMarkdown };
}
